import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { dateTime } from '../lib/format';

/**
 * CM-3b — the forms an operator has built.
 *
 * A form belongs to one collection point: it is how that point collects. So a
 * new form starts by choosing the point, and inherits its purposes as a
 * checkbox each.
 */

interface FormRow {
  id: string;
  key: string;
  name: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  liveVersion: number | null;
  collectionPointId: string;
  collectionPointName: string;
  createdAt: string;
}

interface PointRow {
  id: string;
  name: string;
  channel: string;
  purposeIds: string[];
}

export default function Forms() {
  const user = getUser();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  const writes = can(user, 'forms:edit');

  const forms = useResource<{ forms: FormRow[] }>(() => api.get('/v1/forms'), [reload]);
  const rows = forms.data?.forms ?? [];

  return (
    <>
      <PageHead
        title="Consent forms"
      >
        {writes && <button className="primary" onClick={() => setCreating(true)}>New form</button>}
      </PageHead>

      <Card title="Forms">
        {forms.loading && <Loading what="Loading forms" />}
        <ErrorNote error={forms.error} />
        {!forms.loading && !rows.length && (
          <Empty>No forms.</Empty>
        )}
        {!!rows.length && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Form</th>
                  <th>Collects for</th>
                  <th>Status</th>
                  <th>Live version</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <Link to={`/forms/${f.id}`}><strong>{f.name}</strong></Link>
                    </td>
                    <td className="small">{f.collectionPointName}</td>
                    <td>
                      {f.status === 'published'
                        ? <Pill tone="ok">Published</Pill>
                        : <Pill>{f.status}</Pill>}
                    </td>
                    <td className="small muted">{f.liveVersion ? `v${f.liveVersion}` : 'never published'}</td>
                    <td className="small muted">{dateTime(f.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <NewForm
          taken={(forms.data?.forms ?? []).map((x) => x.collectionPointId)}
          onClose={() => setCreating(false)}
          onCreated={(name) => {
            setCreating(false);
            setReload((n) => n + 1);
            toast(`"${name}" created. Add your fields.`, 'ok');
          }}
        />
      )}
    </>
  );
}

function NewForm({ onClose, onCreated, taken }: { onClose: () => void; onCreated: (name: string) => void; taken: string[] }) {
  const points = useResource<{ collectionPoints: PointRow[] }>(() => api.get('/v1/collection-points'));
  const [collectionPointId, setPoint] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A cookie banner has no form: the banner *is* its interface. And a point
  // holds one form, so points that already have one are not offered.
  const usable = (points.data?.collectionPoints ?? []).filter((p) => p.channel !== 'cookie' && !taken.includes(p.id));

  async function create() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/forms', { collectionPointId, name });
      onCreated(name);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New form"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={create} disabled={busy || !name || !collectionPointId}>
            {busy ? 'Creating…' : 'Create form'}
          </button>
        </>
      }
    >
      <Field label="Where the form is used" help="Each form belongs to one collection point, and a point holds one form.">
        <select value={collectionPointId} onChange={(e) => setPoint(e.target.value)}>
          <option value="">Choose a collection point</option>
          {usable.map((p) => (
            <option key={p.id} value={p.id}>{p.name} · {p.purposeIds.length === 1 ? '1 purpose' : `${p.purposeIds.length} purposes`}</option>
          ))}
        </select>
      </Field>
      <Field label="Form name" help="Internal.">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Membership application" />
      </Field>
      {!points.loading && !usable.length && (
        <div className="note warn">
          Every website or app collection point already has a form. <Link to="/collection-points">Add a collection point</Link> for the new form first.
        </div>
      )}
      <ErrorNote error={error} />
    </Modal>
  );
}
